import {action, computed, observable} from 'mobx'
import {notification} from 'antd'
import Validator from 'validatorjs'
import {mergeDeep} from 'Utils'
import {getSingleDefaults} from './consts/defaults'

import {Condition} from './condition'

export class Single {
  @observable data
  @observable loading = false
  @observable changed = false
  @observable errors = {}
  @observable filter = ''
  @observable selectedRowKeys = []
  @observable loadingAdditional = false
  @observable additionalTickersNames = []

  constructor(rootStore, api) {
    this.root = rootStore
    this.api = api
    this.data = mergeDeep({}, getSingleDefaults(api))
  }

  @computed get foundTickers() {
    return this.data.selected_tickers.tickers.filter(({ticker, data}) => {
      const filter = this.filter.toLowerCase()
      if (typeof ticker === 'object') {
        if (ticker.symbol.toLowerCase().indexOf(filter) !== -1) {
          return true
        }
        if (ticker.symbol_name.toLowerCase().indexOf(filter) !== -1) {
          return true
        }
      } else {
        if (ticker.toLowerCase().indexOf(filter) !== -1) {
          return true
        }
      }

      for (let key in data) {
        if (key === '0') {
          continue
        }
        if (`${data[key]}`.toLowerCase().indexOf(filter) !== -1) {
          return true
        }
      }
      return false
    })
  }

  @action change(key, value) {
    this.data[key] = value
    this.changed = true
  }

  @action blank() {
    this.data = mergeDeep({}, getSingleDefaults(this.api))
    this.changed = false
  }

  @action addCondition() {
    this.data.conditions.push(new Condition(this.api, {}))
  }

  @action removeCondition(index) {
    this.data.conditions.splice(index, 1)
  }

  @action filterTickers(value) {
    this.filter = value
  }

  @action selectRow(keys) {
    this.selectedRowKeys = keys
  }

  @action deleteTickers() {
    this.data.selected_tickers.tickers = this.data.selected_tickers.tickers.filter(row => {
      const rowKey = typeof row.ticker === 'object' ? row.ticker.symbol : row.ticker
      return this.selectedRowKeys.indexOf(rowKey) === -1
    })
    this.selectRow([])
  }

  @action async load(id) {
    this.loading = true
    try {
      const {condition, ...data} = await this.api.request(`sys/stock-filters/${id}`, 'get')
      const {conditions, custom_logic} = condition
      this.data = {
        ...data,
        custom_logic,
        conditions: conditions.map(data => new Condition(this.api, data))
      }
      this.addCondition()
    } catch (e) {
      if (e.status_code === 404) {
        this.root.redirectTo({pathname: '/not-found'})
        return
      }
      notification['error']({
        message: 'Failed to load stock filter',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
      this.changed = false
    }
  }

  @action create(preparingAlgo = false) {
    this.save('create', preparingAlgo)
  }

  @action update(preparingAlgo = false) {
    this.save('update', preparingAlgo)
  }

  @action async save(type, preparingAlgo) {
    const validation = this.validateSave()
    if (validation.fails()) {
      this.errors = validation.errors.all()
      return
    }

    this.loading = true

    if (!this.data.selected_tickers?.tickers?.length) {
      this.loading = false
      notification['warning']({
        message: 'Please add at least one ticker'
      })
      return
    }

    try {
      const method = type === 'update' ? 'put' : 'post'
      const {id} = await this.api.request('sys/stock-filters', method, this.createBody())
      this.change('id', id)
      if (preparingAlgo) {
        this.root.redirectTo({
          pathname: '/builder',
          query: {stock_filter:id}
        })
      } else {
        this.root.redirectTo({
          pathname: '/stock-filters'
        })
      }
    } catch (e) {
      notification['error']({
        message: `Failed to ${type} stock filter`,
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }

  @action validateSave() {
    const rules = {
      name: 'required|max:100'
    }
    const data = {
      name: this.data.name
    }
    return new Validator(data, rules)
  }

  @action createBody() {
    const {conditions, custom_logic, ...data} = this.data
    return {
      condition: {
        conditions: conditions.filter(condition => condition.validate()).map(condition => condition.transform()),
        custom_logic
      },
      ...data
    }
  }

  @action changeAdditional(value) {
    this.additionalTickersNames = value
    this.changed = true
  }

  @action async loadTagsData() {
    const tags = this.data.conditions.reduce((result, el) => {
      if (el.tag) {
        result.push(el.tag.toLowerCase().replace(/\s/g, ''))
      }
      return result
    }, [])
    if (!this.additionalTickersNames.length) {
      this.errors = {additional: 'Please add at least one ticker.'}
      return
    }

    this.loadingAdditional = true

    const selected = this.data.selected_tickers.tickers.map(row => {
      if (typeof row.ticker === 'object') {
        return row.ticker.symbol
      }
      return row.ticker
    })

    const tickers = this.additionalTickersNames.filter(additional => additional && selected.indexOf(additional) === -1)

    const body = {
      tickers: tickers.map(ticker => ({
        ticker: ticker.toUpperCase().trim(),
        tags
      }))
    }

    try {
      const {tickers} = await this.api.request('sys/stock-filters/load-tags-data', 'post', body)
      tickers.map(ticker => {
        this.data.selected_tickers.tickers.push(ticker)
      })
      this.additionalTickersNames = []
    } catch (e) {
      notification['error']({
        message: 'Failed to load tags data',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loadingAdditional = false
    }
  }

  @action removeError(name) {
    delete this.errors[name]
  }
}
